import { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '../ds';
import { font } from '../tokens';
import type { AppCapabilitySelection } from '../api';
import './access-explainer.css';
import './capability-guide.css';

interface CapabilityInfo {
  key: keyof AppCapabilitySelection;
  label: string;
  summary: string;
  examples: readonly string[];
  gcpService: string;
}

interface CapabilityGuideProps {
  services: readonly CapabilityInfo[];
  selection: AppCapabilitySelection;
  enabled?: AppCapabilitySelection;
  pending?: AppCapabilitySelection;
  onSelect: (key: keyof AppCapabilitySelection) => void;
}

export function CapabilityGuide(props: CapabilityGuideProps) {
  const [open, setOpen] = useState(false);
  return <>
    <button type="button" className="access-explainer-link" aria-haspopup="dialog" onClick={() => setOpen(true)}>
      Which capabilities do I need?
    </button>
    {open && createPortal(<CapabilityGuideDialog {...props} onClose={() => setOpen(false)} />, document.body)}
  </>;
}

function CapabilityGuideDialog({ services, selection, enabled, pending, onSelect, onClose }: CapabilityGuideProps & { onClose: () => void }) {
  const id = useId();
  const dialog = useRef<HTMLDialogElement>(null);
  const pills = useRef<Partial<Record<keyof AppCapabilitySelection, HTMLButtonElement | null>>>({});
  const [selected, setSelected] = useState(services[0]?.key);
  const active = services.find(service => service.key === selected);
  const selectedCount = services.filter(service => selection[service.key] && !enabled?.[service.key] && !pending?.[service.key]).length;

  useEffect(() => {
    const element = dialog.current;
    const previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    element?.showModal();
    return () => {
      element?.close();
      previousFocus?.focus();
    };
  }, []);

  return <dialog ref={dialog} className="access-explainer capability-guide" style={{ fontFamily: font }} aria-labelledby={`${id}-title`} aria-describedby={`${id}-description`}
    onCancel={event => { event.preventDefault(); onClose(); }}
    onClick={event => {
      if (event.target !== event.currentTarget) return;
      const bounds = event.currentTarget.getBoundingClientRect();
      if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) onClose();
    }}>
    <header className="access-explainer-header">
      <h2 id={`${id}-title`}>Which capabilities do I need?</h2>
      <Button variant="outlined" size="sm" onPress={onClose}>Close</Button>
    </header>
    <p id={`${id}-description`} className="capability-guide-summary">These capabilities are optional. It’s perfectly fine to leave them all off if your app doesn’t need them.</p>
    <div className="environment-selector access-explainer-pills capability-guide-pills" role="group" aria-label="App capabilities">
      {services.map(service => <button type="button" key={service.key} ref={element => { pills.current[service.key] = element; }} aria-label={service.label} aria-pressed={selected === service.key}
        onClick={() => setSelected(service.key)}>
        {(selection[service.key] || enabled?.[service.key] || pending?.[service.key]) && <span aria-hidden="true">✓ </span>}{service.label}
      </button>)}
    </div>
    <span className="access-explainer-announcement" role="status" aria-live="polite">{active?.label}</span>
    <div className="capability-guide-panes">
      {services.map(service => <section key={service.key} className="capability-guide-pane" data-active={selected === service.key}
        aria-hidden={selected !== service.key} aria-label={`${service.label} explanation`}>
        <p className="capability-guide-summary">{service.summary}</p>
        {service.key === 'cloudScheduler' && <p className="capability-guide-summary">Scheduled Tasks start a separate Background Job; they do not schedule or restart your main web app.</p>}
        <h4>When you need it</h4>
        <ul>{service.examples.map(example => <li key={example}>{example}</li>)}</ul>
        <div className="capability-guide-action">
          {enabled?.[service.key] ? <span className="capability-guide-state">Already enabled</span>
            : pending?.[service.key] ? <span className="capability-guide-state">Pending review</span>
              : <Button variant={selection[service.key] ? 'secondary' : 'primary'} size="sm"
                disabled={selection[service.key] || selected !== service.key}
                onPress={() => {
                  onSelect(service.key);
                  pills.current[service.key]?.focus();
                }}>
                {selection[service.key] ? '✓ Selected' : 'I need this!'}
              </Button>}
        </div>
        <p className="capability-guide-provider">GCP service: {service.gcpService}</p>
      </section>)}
    </div>
    {selectedCount > 0 && <p className="capability-guide-selection-count" role="status">{selectedCount} {selectedCount === 1 ? 'capability' : 'capabilities'} selected for this request.</p>}
  </dialog>;
}
